import type { SubmitHandler } from 'react-hook-form';

import type { IContainerProps } from '~/components/utils/Container';
import type {
  IApiCreateLegalDocumentRequestBody,
  IApiCreateLegalDocumentResponseBody,
} from '~/lib/api/modules/LegalDocuments';
import type { IUseCreateLegalDocumentMutationValues } from '~/lib/api/modules/LegalDocuments/hooks/useCreateLegalDocumentMutation';
import type { ILegalDocumentFormValues } from './LegalDocumentForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { markdownToHtml } from '~/helpers/markdownToHtml';
import { useCreateLegalDocumentMutation } from '~/lib/api/modules/LegalDocuments/hooks/useCreateLegalDocumentMutation';
import { LegalDocumentForm } from './LegalDocumentForm';

export type ICreateLegalDocumentFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiCreateLegalDocumentResponseBody,
    inputValues: IUseCreateLegalDocumentMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
};

export const CreateLegalDocumentForm: React.FC<
  ICreateLegalDocumentFormProps
> = (props) => {
  const { sx, onSuccess, cancelUrl, appId } = props;
  const createLegalDocumentMutation = useCreateLegalDocumentMutation();

  const handleSubmit: SubmitHandler<ILegalDocumentFormValues> = (values) => {
    if (createLegalDocumentMutation.isLoading) {
      return;
    }

    return markdownToHtml(values.markdown!).then((html) => {
      const body: IApiCreateLegalDocumentRequestBody = {
        type: values.type!,
        markdown: values.markdown!,
        html,
        title: values.title!,
        outdated: !!values.outdated,
        required: !!values.required,
      };

      return createLegalDocumentMutation.mutateAsync(
        { appId, body },
        { onSuccess },
      );
    });
  };

  return (
    <ContentBlock sx={sx}>
      <LegalDocumentForm
        first
        mode="creation"
        onSubmit={handleSubmit}
        cancelUrl={cancelUrl}
      />
    </ContentBlock>
  );
};
